import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';

import { links } from '../../paths';

import { useDispatch, useSelector } from '../../store/utils';
import { variablesChange } from '../../store/actions';

import { DialogClose } from '../../atoms/dialog-close';
import { ParentPaper } from '../../atoms/parent-paper';

import { InputBox } from '../../molecules/input-box';
import { InverterTypeSelect } from '../../organisms/inverter-type-select';
import { WallboxTypeSelect } from '../../organisms/wallbox-type-select';
import { SettingsInstallation } from '../settings/boxes/settings-installation';
import { SettingsCircuitBreaker } from '../settings/boxes/settings-circuit-breaker';
import { SettingsPvStringNumber } from '../settings/boxes/settings-pv-string-number';
import { SettingsPvBattery } from '../settings/boxes/settings-pv-battery';
import { SettingsPvOverflow } from '../settings/boxes/settings-pv-overflow';
import { SettingsPvNominal } from '../settings/boxes/settings-pv-nominal';
import { SettingsHw } from '../settings/boxes/settings-hw';
import { SettingsSpInstall } from '../settings/boxes/settings-sp-install';
import { SettingsWbInstall } from '../settings/boxes/settings-wb-install';
import { SettingsSpLocation } from '../settings/boxes/settings-sp-location';
import { SettingsPvInverterStatus } from '../settings/boxes/settings-pv-inverter-status';

import { ControlBox } from '../../atoms/control-box';
import { AppUpdate } from '../settings/app-update';
import { BoilerTestTrigger } from '../../organisms/boiler-test-wizard/boiler-test-trigger';

const serviceMenuPathImage = new URL(
  '../../static/service-menu-path.gif',
  import.meta.url
);

const TOTAL_STEPS = 11;

export const useInitialWizardDialog = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const wizardState = (location.state as any)?.wizard;
  const open = wizardState?.open === true;
  const currentStep = wizardState?.step || 1;

  const handleOpen = () => {
    navigate(location.pathname + location.search, {
      state: {
        ...location.state,
        wizard: { open: true, step: 1 },
      },
    });
  };

  const handleClose = () => {
    const newState = { ...location.state };
    delete newState.wizard;
    navigate(location.pathname + location.search, {
      state: newState,
    });
  };

  const setCurrentStep = (step: number) => {
    navigate(location.pathname + location.search, {
      state: {
        ...location.state,
        wizard: { open: true, step },
      },
    });
  };

  const dialog = (
    <InitialWizardDialog
      open={open}
      onClose={handleClose}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
    />
  );

  return {
    dialog,
    open: handleOpen,
    close: handleClose,
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
};

const InitialWizardDialog = ({
  open,
  onClose,
  currentStep,
  setCurrentStep,
}: Props) => {
  const dispatch = useDispatch();
  const serviceActive = useSelector((s) => s.variables.SERVICE_ACTIVE);
  const location = useLocation();
  const prevPathnameRef = React.useRef(location.pathname);

  useEffect(() => {
    if (open && location.pathname !== prevPathnameRef.current) {
      onClose();
    }
    prevPathnameRef.current = location.pathname;
  }, [location.pathname, open, onClose]);

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete wizard
      dispatch(
        variablesChange({
          INITIAL_WIZARD_COMPLETED_AT: new Date().toISOString(),
        })
      );
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onClose();
    }
  };

  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'secondary.main',
          },
        },
      }}
    >
      <DialogClose onClose={onClose} />

      <DialogTitle
        sx={{
          textAlign: 'center',
          pt: 4,
          pb: 2,
        }}
      >
        <Typography variant="h3">
          <FormattedMessage
            id="initial_wizard.dialog.title"
            defaultMessage="Průvodce počátečním nastavením"
          />
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
          <FormattedMessage
            id="initial_wizard.dialog.step"
            defaultMessage="Krok {current} z {total}"
            values={{ current: currentStep, total: TOTAL_STEPS }}
          />
        </Typography>
      </DialogTitle>

      <Box sx={{ px: 2 }}>
        <LinearProgress variant="determinate" value={progress} />
      </Box>

      <DialogContent
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 0,
          pt: 3,
        }}
      >
        <Container>
          <WizardStep step={currentStep} />
        </Container>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          px: 3,
          py: 2,
        }}
      >
        <Button onClick={handlePrev} variant="outlined">
          <FormattedMessage
            id="initial_wizard.dialog.prev"
            defaultMessage="Zpět"
          />
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          disabled={currentStep === 1 && !serviceActive}
        >
          {currentStep === TOTAL_STEPS ? (
            <FormattedMessage
              id="initial_wizard.dialog.finish"
              defaultMessage="Dokončit"
            />
          ) : (
            <FormattedMessage
              id="initial_wizard.dialog.next"
              defaultMessage="Další"
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WizardStep = ({ step }: { step: number }) => {
  switch (step) {
    case 1:
      return <Step1ServiceLogin />;
    case 2:
      return <Step2AppUpdate />;
    case 3:
      return <Step3Installation />;
    case 4:
      return <Step4CircuitBreaker />;
    case 5:
      return <Step5PVInverter />;
    case 6:
      return <Step6PVDetails />;
    case 7:
      return <Step7PVOverflow />;
    case 8:
      return <Step8Boiler />;
    case 9:
      return <Step9SpotPrices />;
    case 10:
      return <Step10Wallbox />;
    case 11:
      return <Step11Done />;
    default:
      return null;
  }
};

// Step 1: Service Login
const Step1ServiceLogin = () => {
  const serviceActive = useSelector((s) => s.variables.SERVICE_ACTIVE);

  return (
    <Stack gap={2}>
      <Typography variant="h2" sx={{ textAlign: 'center' }}>
        <FormattedMessage
          id="initial_wizard.step1.title"
          defaultMessage="Přístup do servisu"
        />
      </Typography>
      {serviceActive ? (
        <>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            <FormattedMessage
              id="initial_wizard.step1.description.active"
              defaultMessage="Váš přístup do servisu je již aktivní. Můžete pokračovat na další krok."
            />
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
            <FormattedMessage
              id="initial_wizard.step1.welcome"
              defaultMessage="Vítejte v úvodním nastavení Infigy. Provedeme vás základní konfigurací vašeho systému. Detailnější nastavení poté najdete v servisním menu."
            />
          </Typography>

          <ParentPaper>
            <InputBox
              title={
                <FormattedMessage
                  id="settings.service.password"
                  defaultMessage="Přístup do servisu"
                />
              }
              inputVariableId="SERVICE_PASSWORD"
              label={
                <FormattedMessage
                  id="settings.service.password.label"
                  defaultMessage="Heslo"
                />
              }
              fullWidth
            />
            <Paper>
              <Typography variant="body1" sx={{ color: 'primary.main' }}>
                <FormattedMessage
                  id="initial_wizard.step1.password_info"
                  defaultMessage="Každá firma má vlastní přístupové heslo do servisního menu. Heslo jste obdrželi v emailové komunikaci. Můžete si jej také kdykoliv zobrazit v app.infigy.cz v partnerské sekci."
                />
              </Typography>
            </Paper>
          </ParentPaper>
        </>
      )}
    </Stack>
  );
};

// Step 2: App Update
const Step2AppUpdate = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="initial_wizard.step2.title"
          defaultMessage="Aktualizace aplikace"
        />
      </Typography>
      <ParentPaper>
        <AppUpdate autoCheck />
        <Paper>
          <Typography variant="body1" sx={{ color: 'primary.main' }}>
            <FormattedMessage
              id="initial_wizard.step_app_update.description"
              defaultMessage="V čase kdy bylo Infigy na cestě k vám jsme mohli vydat nové verze aplikace. Doporučujeme před nastavením jednotku aktualizovat. Pokud ale například není dostupný internet, můžete aktualizaci přeskočit a provést ji později v servisním menu, nebo v portálu."
            />
          </Typography>
        </Paper>
      </ParentPaper>
    </Stack>
  );
};

// Step 3: Installation
const Step3Installation = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="initial_wizard.step3.title"
          defaultMessage="Instalace"
        />
      </Typography>
      <ParentPaper>
        <SettingsInstallation />
      </ParentPaper>
    </Stack>
  );
};

// Step 4: Circuit Breaker
const Step4CircuitBreaker = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="initial_wizard.step4.title"
          defaultMessage="Nastavení hlavního jističe"
        />
      </Typography>
      <ParentPaper>
        <SettingsCircuitBreaker />
      </ParentPaper>
    </Stack>
  );
};

// Step 5: PV Inverter
const Step5PVInverter = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="initial_wizard.step5.title"
          defaultMessage="Připojení střídače"
        />
      </Typography>
      <ParentPaper>
        <InverterTypeSelect />
        <SettingsPvInverterStatus />
        <SettingsPvStringNumber />
      </ParentPaper>
    </Stack>
  );
};

// Step 6: PV Details
const Step6PVDetails = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="initial_wizard.step6.title"
          defaultMessage="Parametry střídače"
        />
      </Typography>
      <ParentPaper>
        <SettingsPvNominal />
      </ParentPaper>
      <ParentPaper>
        <SettingsPvBattery />
      </ParentPaper>
    </Stack>
  );
};

// Step 7: PV Overflow
const Step7PVOverflow = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="initial_wizard.step7.title"
          defaultMessage="Omezení přetoku"
        />
      </Typography>
      <ParentPaper>
        <SettingsPvOverflow />
        <Paper>
          <Typography variant="body1" sx={{ color: 'primary.main' }}>
            <FormattedMessage
              id="initial_wizard.step7.description"
              defaultMessage="Pokud je omezen přetok do sítě, dojde k přenesení nastavení do střídače. Omezování přetoků zajištuje střídač. Zápis a změnu hodnoty však řídí Infigy dle zadané hodnoty v tomto poli. Neupravujte tedy omezení přetoku ve střídači. Při prvním nastavení si zapsanou hodnotu zkontrolujte ve střídači, zda se Vám správně promítla z Infigy do střídače."
            />
          </Typography>
        </Paper>
      </ParentPaper>
    </Stack>
  );
};

// Step 8: Boiler
const Step8Boiler = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="initial_wizard.step8.title"
          defaultMessage="Nastavení bojleru"
        />
      </Typography>
      <ParentPaper>
        <SettingsHw />
        <BoilerTestTrigger variant="dialog" />
      </ParentPaper>
    </Stack>
  );
};

// Step 9: Spot Prices
const Step9SpotPrices = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="initial_wizard.step9.title"
          defaultMessage="Nastavení spotového obchodování"
        />
      </Typography>
      <ParentPaper>
        <SettingsSpInstall />
        <SettingsSpLocation />
      </ParentPaper>
    </Stack>
  );
};

// Step 10: Wallbox
const Step10Wallbox = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="initial_wizard.step10.title"
          defaultMessage="Nastavení wallboxu"
        />
      </Typography>
      <ParentPaper>
        <SettingsWbInstall />
        <WallboxTypeSelect />
      </ParentPaper>
    </Stack>
  );
};

// Step 11: Done
const Step11Done = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return (
    <>
      <Typography variant="h2" sx={{ textAlign: 'center' }}>
        <FormattedMessage
          id="initial_wizard.step11.title"
          defaultMessage="Hotovo"
        />
      </Typography>
      <ParentPaper sx={{ mt: 2 }}>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="initial_wizard.step11.description"
              defaultMessage="Prošli jste základní nastavení Infigy. Veškeré parametry lze upravit v servisním menu."
            />
          </Typography>
        </Paper>
        <ControlBox
          title={
            <FormattedMessage
              id="initial_wizard.step11.service_menu.title"
              defaultMessage="Servisní menu"
            />
          }
          subtitle={
            <FormattedMessage
              id="initial_wizard.step11.service_menu.subtitle"
              defaultMessage="Nastavení -> úplně dolů -> Přístup do servisu"
            />
          }
          fullWidth
          control={
            <IconButton
              onClick={() => {
                dispatch(
                  variablesChange({
                    INITIAL_WIZARD_COMPLETED_AT: new Date().toISOString(),
                  })
                );
                navigate(links.settings, {
                  state: { scroll: 'service' },
                });
              }}
            >
              <OpenInNew />
            </IconButton>
          }
        />
        <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
          <Box
            component="img"
            src={serviceMenuPathImage.toString()}
            alt=""
            sx={{ width: 260, height: 'auto' }}
          />
        </Box>
      </ParentPaper>
    </>
  );
};
